import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { MapPin, ArrowRight, Sparkles } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

export default function GrowersPage() {
  const [growers, setGrowers] = useState([]);
  const [publishedById, setPublishedById] = useState({}); // keyed by grower_id (application id)
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("");
  const { t } = useSiteContent();

  useEffect(() => {
    Promise.all([
      api.get("/public/growers"),
      api.get("/public/growers/published").catch(() => ({ data: { growers: [] } })),
    ]).then(([cards, published]) => {
      setGrowers(cards.data.growers || []);
      const map = {};
      for (const p of published.data.growers || []) {
        if (p.grower_id) map[p.grower_id] = p;
      }
      setPublishedById(map);
      setLoading(false);
    });
  }, []);

  const regions = useMemo(
    () => Array.from(new Set(growers.map((g) => g.region).filter(Boolean))).sort(),
    [growers]
  );
  const filtered = region ? growers.filter((g) => g.region === region) : growers;

  return (
    <div className="club-theme min-h-screen" data-testid="growers-page">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
        <div className="bloom-divider mb-6">✦ {t("growers.eyebrow", "Chapter II · The Network")} ✦</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-3xl">
          {t("growers.headline", "Meet our growers.")}
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl">
          {t("growers.sub", "A small, independent network of flower farms across Aotearoa. Each one shaping the Bloom Club from their own corner of the country.")}
        </p>
        {regions.length > 0 && (
          <div className="mt-10 flex items-center gap-3 flex-wrap" data-testid="growers-filter">
            <span className="bloom-label">Filter by region</span>
            <button onClick={() => setRegion("")} className={`px-3 py-1 text-xs tracking-[0.15em] uppercase border ${region === "" ? "border-[var(--bloom-brand)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)]"}`}>All</button>
            {regions.map((r) => (
              <button key={r} onClick={() => setRegion(r)} className={`px-3 py-1 text-xs tracking-[0.15em] uppercase border ${region === r ? "border-[var(--bloom-brand)] bg-[var(--bloom-surface)]" : "border-[var(--bloom-border)]"}`}>{r}</button>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="growers-grid-section">
        {loading ? (
          <div className="text-center py-24 font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bloom-card p-14 text-center" data-testid="growers-empty">
            <Sparkles className="mx-auto mb-3 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl mb-2">No growers in this region yet.</h3>
            <p className="font-serif italic text-[var(--bloom-ink-soft)] max-w-md mx-auto mb-6">
              We&apos;re carefully accepting Founding Growers one region at a time. Perhaps your farm is next?
            </p>
            <Link to="/become-a-grower" className="bloom-btn bloom-btn-primary">Become a grower</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {filtered.map((g) => {
              const pub = publishedById[g.id];
              // Prefer published profile photo + farm name if available
              const heroPhoto = pub?.hero_photo_url || g.photo;
              const farmName = pub?.farm_name || g.farm_name;
              const tagline = pub?.tagline || g.vision;
              const varieties = (pub?.varieties && pub.varieties.length > 0) ? pub.varieties : g.crops;
              const detailHref = pub ? `/growers/${pub.grower_id || pub.id}` : null;

              const Card = (
                <>
                  <div className="aspect-[4/5] overflow-hidden bg-[var(--bloom-surface)] border border-[var(--bloom-border)] mb-5">
                    {heroPhoto ? (
                      <img src={heroPhoto} alt={farmName} className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.02]" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--bloom-ink-muted)] italic font-serif p-6 text-center">{farmName}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-2">
                    <MapPin size={11} /> {g.town_city ? `${g.town_city}, ` : ""}{g.region || "Aotearoa"}
                    {g.is_active && <span className="ml-2 text-[var(--bloom-brand)]">✦ Active</span>}
                  </div>
                  <h3 className="font-serif text-3xl leading-tight mb-1">{farmName || ""}</h3>
                  <div className="font-serif italic text-base text-[var(--bloom-ink-soft)] mb-3">{pub?.display_name || g.name}</div>
                  {tagline && <p className="font-serif italic text-sm text-[var(--bloom-ink)] leading-relaxed">&quot;{tagline}&quot;</p>}
                  {varieties && varieties.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {varieties.slice(0, 6).map((c, i) => (
                        <span key={`${c}-${i}`} className="text-[10px] tracking-[0.15em] uppercase border border-[var(--bloom-border)] px-2 py-0.5">{c}</span>
                      ))}
                    </div>
                  )}
                  {detailHref && (
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[var(--bloom-brand)] group-hover:gap-3 transition-all" data-testid={`grower-card-view-link-${g.id}`}>
                      View profile <ArrowRight size={14} />
                    </div>
                  )}
                </>
              );

              return detailHref ? (
                <Link
                  key={g.id}
                  to={detailHref}
                  className="group block focus:outline-none focus:ring-2 focus:ring-[var(--bloom-brand)] focus:ring-offset-2"
                  data-testid={`grower-card-${g.id}`}
                >
                  {Card}
                </Link>
              ) : (
                <article key={g.id} className="group" data-testid={`grower-card-${g.id}`}>
                  {Card}
                </article>
              );
            })}
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
